import React from "react";
import { Link, useNavigate } from "react-router-dom";
import logoImg from "../../assets/images/logo1.png";

const browserLinks = [
  { label: "Microsoft Edge", href: "https://support.microsoft.com/microsoft-edge" },
  { label: "Firefox", href: "https://support.mozilla.org/kb/enhanced-tracking-protection-firefox-desktop" },
  { label: "Chrome", href: "https://support.google.com/chrome/answer/95647" },
  { label: "Safari", href: "https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" },
  { label: "Opera", href: "https://help.opera.com/en/latest/web-preferences/#cookies" },
];

function CookiePolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <header className="w-full border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center px-4 py-6">
          <Link to="/" aria-label="MoveRelief home">
            <img src={logoImg} alt="MoveRelief logo" className="h-16 w-16 object-contain" />
          </Link>
        </div>
      </header>

      <div className="w-full bg-[#7C3A8D]">
        <div className="mx-auto max-w-5xl px-4 py-8">
          <h1 className="text-3xl font-bold text-white md:text-4xl">Cookie Policy</h1>
        </div>
      </div>

      <main className="w-full">
        <div className="mx-auto max-w-3xl px-4 py-10 text-left text-base leading-relaxed text-gray-800">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-6 text-sm font-medium text-gray-600 hover:text-[#7C3A8D]"
          >
            ← Back
          </button>

          <h2 className="mb-4 text-2xl font-bold">Cookie Policy</h2>

          <p className="mb-4">
            This Cookie Policy explains how cookies and similar technologies are used on MoveRelief.pro
            and related services operated by Momiji Group Inc. It describes how cookies help us provide
            the MoveRelief website, improve the experience, support checkout and account features, and
            understand how users interact with our back relief plan and posture scan flows.
          </p>

          <p className="mb-4">
            MoveRelief oversees the collection of information through cookies placed on your device. For
            questions or further information, contact us at{" "}
            <a className="text-[#7C3A8D] underline" href="mailto:info@moverelief.pro">
              info@moverelief.pro
            </a>
            .
          </p>

          <p className="mb-4">
            This Cookie Policy should be read together with our{" "}
            <Link className="text-[#7C3A8D] underline" to="/privacy">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link className="text-[#7C3A8D] underline" to="/terms">
              Terms and Conditions
            </Link>
            .
          </p>

          <p className="mb-8">
            By interacting with MoveRelief.pro, you consent to our use of cookies in accordance with this
            Cookie Policy, unless you adjust your browser or device settings to reject cookies.
          </p>

          <section className="mb-8">
            <h3 className="mb-2 text-xl font-bold">General Information</h3>
            <p className="mb-4">
              If you prefer not to allow cookies on your device, you can adjust your internet browser
              settings to reject some or all cookies and to alert you when a cookie is set. Doing so may
              affect your ability to use or access certain parts of the website, including checkout,
              account access, personalization, and scan-related features.
            </p>
            <p className="mb-4">
              Cookies already stored on your device can usually be deleted manually at any time. This will
              not prevent MoveRelief from setting new cookies unless you also adjust your browser settings.
            </p>

            <p className="mb-2">For managing and blocking cookies, consult the guides below:</p>
            <ul className="mb-4 list-disc space-y-1 pl-6">
              {browserLinks.map((link) => (
                <li key={link.label}>
                  <a
                    className="text-[#7C3A8D] underline hover:text-[#5F2D6F]"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            <p className="mb-2">Mobile users can manage cookies through their device settings:</p>
            <ul className="list-disc space-y-1 pl-6">
              <li>
                For Android, refer to{" "}
                <a
                  className="text-[#7C3A8D] underline hover:text-[#5F2D6F]"
                  href="https://support.google.com/accounts/answer/61416"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Support
                </a>
                .
              </li>
              <li>
                For iOS, visit{" "}
                <a
                  className="text-[#7C3A8D] underline hover:text-[#5F2D6F]"
                  href="https://support.apple.com/en-us/105082"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Apple Support
                </a>
                .
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="mb-2 text-xl font-bold">Cookies Utilized by MoveRelief</h3>
            <p className="mb-4">
              MoveRelief may use several categories of cookies and similar technologies that support
              website functionality, security, payment flows, performance analytics, personalization, and
              advertising measurement.
            </p>

            <ul className="mb-4 list-disc space-y-1 pl-6">
              <li>Type: Essential, Security, Functional, Analytics, Advertising, and Performance.</li>
              <li>Duration: Session or persistent, depending on the cookie purpose.</li>
              <li>
                Purpose: Provide website functionality, support checkout and account access, secure the
                platform, remember preferences, measure campaigns, and improve our services.
              </li>
            </ul>

            <p className="mb-4">
              We may use Google Tag Manager, Google Analytics, advertising pixels, payment-provider
              cookies, and other third-party tools to understand how users interact with MoveRelief.pro and
              improve the experience. For more information about Google data practices, review the{" "}
              <a
                className="text-[#7C3A8D] underline hover:text-[#5F2D6F]"
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
              .
            </p>

            <p>
              Third-party providers may set their own cookies when their services are used on our website.
              Their use of cookies is governed by their respective privacy and cookie policies.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="mb-2 text-xl font-bold">Contact Us</h3>
            <p>
              For any questions or if you seek clarification regarding our Cookie Policy or how we handle
              your data, contact us at{" "}
              <a className="text-[#7C3A8D] underline" href="mailto:info@moverelief.pro">
                info@moverelief.pro
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="mx-auto max-w-5xl px-4 py-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="max-w-md">
              <Link to="/" className="mb-4 inline-block" aria-label="MoveRelief home">
                <img src={logoImg} alt="MoveRelief logo" className="h-16 w-16 object-contain" />
              </Link>
              <p className="text-sm leading-relaxed text-gray-400">
                MoveRelief.pro provides personalized back relief plans, posture scan flows, and gentle
                movement guidance designed for everyday comfort.
              </p>
            </div>

            <div>
              <h3 className="mb-3 font-semibold">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/privacy">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/terms">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link className="text-gray-400 hover:text-white" to="/cookie">
                    Cookie Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-800 pt-6 text-sm text-gray-400">
            © 2026 Momiji Group Inc. All Rights Reserved.
            <br />
            2604 Whittier Place, Wilmington, DE, 19808
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CookiePolicy;
